import React from "react";
import PropTypes from "prop-types";
import { MaskedTextBoxComponent } from "@syncfusion/ej2-react-inputs";
import classNames from "classnames";

/**
 * PhoneInput — Syncfusion MaskedTextBox wrapper for US phone number input.
 *
 * Uses a mask of `+1 (000) 000 0000` by default and returns the raw
 * 10-digit numeric string to the caller.
 *
 * @example
 * <Controller
 *   name="mobileNumber"
 *   control={control}
 *   render={({ field }) => (
 *     <PhoneInput value={field.value} onChange={field.onChange} error={errors.mobileNumber?.message} />
 *   )}
 * />
 */
export default function PhoneInput({
  placeholder = "Phone Number",
  value,
  onChange,
  error,
  disabled = false,
  className,
  mask = "+1 (000) 000 0000",
  ...props
}) {
  return (
    <div className={classNames("phone-input-wrapper", className)}>
      <MaskedTextBoxComponent
        mask={mask}
        placeholder={placeholder}
        floatLabelType="Auto"
        value={value ?? ""}
        change={e => {
          const raw = e.maskedValue?.replace(/\D/g, "").slice(1) || "";
          onChange?.(raw);
        }}
        enabled={!disabled}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

PhoneInput.propTypes = {
  /** Floating-label / placeholder text */
  placeholder: PropTypes.string,
  /** Controlled value — raw 10-digit string */
  value: PropTypes.string,
  /** Change handler — receives the raw 10-digit string */
  onChange: PropTypes.func,
  /** Validation error message */
  error: PropTypes.string,
  /** Fully disabled */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
  /** Syncfusion mask pattern */
  mask: PropTypes.string,
};

